// Meeting status and stage labels. Owner: WP-5 (BUILD 1).
//
// One label per (status, stage, notes.status), so a ready meeting whose notes
// are still being written, or failed, never reads as "processing" (F7). The
// copy lives in ns/meetings-notes.ts.
//   capturing                    → Recording
//   processing  sealing          → Saving the recording
//               transcribing     → Transcribing the recording
//               legacy stages    → Preparing the transcript
//   failed                       → Needs attention
//   ready       notes generating → Ready · preparing notes
//               notes stale      → Ready · refreshing notes
//               notes failed     → Ready · notes need attention
//               otherwise        → Ready
import { useNamespaceCopy } from '@/i18n/ns';
import meetingsNotesCopy, { type MeetingsNotesCopyKey } from '@/i18n/ns/meetings-notes';
import type { CompanyMeeting, MeetingNotesStatus, MeetingStage } from './types';

export type MeetingStatusFields = Pick<CompanyMeeting, 'status' | 'stage' | 'notes'>;
export type MeetingsNotesCopy = Readonly<Record<MeetingsNotesCopyKey, string>>;

const PROCESSING: Partial<Record<MeetingStage, MeetingsNotesCopyKey>> = {
  sealing: 'stageSealing',
  transcribing: 'stageTranscribing',
};
const READY_NOTES: Partial<Record<MeetingNotesStatus, MeetingsNotesCopyKey>> = {
  generating: 'rowNotesGenerating',
  stale: 'rowNotesStale',
  failed: 'rowNotesFailed',
};
const FAILED: Partial<Record<MeetingStage, MeetingsNotesCopyKey>> = {
  sealing: 'stageFailedSealing',
  transcribing: 'stageFailedTranscribing',
};

/** The copy key of a processing stage's sentence. */
export const meetingProcessingKey = (stage: MeetingStage): MeetingsNotesCopyKey =>
  PROCESSING[stage] ?? 'stageProcessing';

/** The copy key of a failed meeting's explanation, by the stage that failed. */
export const meetingFailureKey = (stage: MeetingStage): MeetingsNotesCopyKey =>
  FAILED[stage] ?? 'stageFailedOther';

/** The copy key of a meeting's short state label. */
export function meetingStatusKey(meeting: MeetingStatusFields): MeetingsNotesCopyKey {
  if (meeting.status === 'capturing') return 'stageCapturing';
  if (meeting.status === 'processing') return meetingProcessingKey(meeting.stage);
  if (meeting.status === 'failed') return 'rowFailed';
  return (meeting.notes && READY_NOTES[meeting.notes.status]) || 'stageReady';
}

/**
 * The short state label of a meeting list row, in the language of `copy`
 * (`useNamespaceCopy(meetingsNotesCopy)`, or use `useMeetingStageLabel`).
 */
export const meetingStageLabel = (meeting: MeetingStatusFields, copy: MeetingsNotesCopy) =>
  copy[meetingStatusKey(meeting)];

/** `meetingStageLabel` in the interface language. */
export function useMeetingStageLabel(): (meeting: MeetingStatusFields) => string {
  const copy = useNamespaceCopy(meetingsNotesCopy);
  return (meeting) => meetingStageLabel(meeting, copy);
}

/**
 * The notes banner under the title: what it says and which button it offers.
 *   retryNotes       the notes failed: regenerate them (no speech-to-text call)
 *   regenerateNotes  current or missing notes: regenerate them on request
 *   retryMeeting     a legacy failed meeting whose transcript is saved: retry
 *                    it, so it becomes ready and the notes lane takes over
 * While the lane works (generating, stale) there is no button. Null: no banner
 * (the meeting is not ready and has no saved transcript).
 */
export type MeetingNotesAction = 'retryNotes' | 'regenerateNotes' | 'retryMeeting';
export type MeetingNotesBanner = {
  message: MeetingsNotesCopyKey;
  action: MeetingNotesAction | null;
};

const NOTES_BANNER: Record<MeetingNotesStatus, MeetingNotesBanner> = {
  none: { message: 'notesNone', action: 'regenerateNotes' },
  generating: { message: 'notesGenerating', action: null },
  stale: { message: 'notesStale', action: null },
  current: { message: 'notesCurrent', action: 'regenerateNotes' },
  failed: { message: 'notesFailed', action: 'retryNotes' },
};

export function meetingNotesBanner(
  meeting: Pick<CompanyMeeting, 'status' | 'notes' | 'segments'>,
): MeetingNotesBanner | null {
  if (meeting.status === 'failed')
    return meeting.segments?.length
      ? { message: 'notesLegacyFailed', action: 'retryMeeting' }
      : null;
  if (meeting.status !== 'ready') return null;
  return NOTES_BANNER[meeting.notes?.status ?? 'none'] ?? null;
}
